// 천간 (10개)
const heavenlyStems = ['갑', '을', '병', '정', '무', '기', '경', '신', '임', '계'];

// 지지 (12개)
const earthlyBranches = ['자', '축', '인', '묘', '진', '사', '오', '미', '신', '유', '술', '해'];

// 오행 매핑
const stemElements: Record<string, string> = {
  '갑': '목', '을': '목',
  '병': '화', '정': '화',
  '무': '토', '기': '토',
  '경': '금', '신': '금',
  '임': '수', '계': '수'
};

const branchElements: Record<string, string> = {
  '인': '목', '묘': '목',
  '사': '화', '오': '화',
  '진': '토', '술': '토', '축': '토', '미': '토',
  '신': '금', '유': '금',
  '자': '수', '해': '수'
};

// 음양 매핑
const stemYinYang: Record<string, '+' | '-'> = {
  '갑': '+', '을': '-',
  '병': '+', '정': '-',
  '무': '+', '기': '-',
  '경': '+', '신': '-',
  '임': '+', '계': '-',
};

// 오행 상생: 木→火, 火→土, 土→金, 金→水, 水→木
const elementGenerates: Record<string, string> = {
  '목': '화', '화': '토', '토': '금', '금': '수', '수': '목',
};

// 오행 상극: 木→土, 土→水, 水→火, 火→金, 金→木
const elementDestroys: Record<string, string> = {
  '목': '토', '토': '수', '수': '화', '화': '금', '금': '목',
};

// 천간 → 오행
const stemToElement: Record<string, string> = {
  '갑': '목', '을': '목',
  '병': '화', '정': '화',
  '무': '토', '기': '토',
  '경': '금', '신': '금',
  '임': '수', '계': '수',
};

// 지지 대표 오행
const branchToElement: Record<string, string> = {
  '인': '목', '묘': '목',
  '사': '화', '오': '화',
  '진': '토', '술': '토', '축': '토', '미': '토',
  '신': '금', '유': '금',
  '자': '수', '해': '수',
};

// 년주 계산 (1900년 = 경자년 기준)
function getYearPillar(year: number): { stem: string; branch: string } {
  const baseYear = 1900; // 경자년
  const baseStemIndex = 6; // 경
  const baseBranchIndex = 0; // 자
  
  const yearDiff = year - baseYear;
  const stemIndex = (baseStemIndex + yearDiff) % 10;
  const branchIndex = (baseBranchIndex + yearDiff) % 12;
  
  return {
    stem: heavenlyStems[stemIndex],
    branch: earthlyBranches[branchIndex]
  };
}

// 월주 계산
function getMonthPillar(year: number, month: number): { stem: string; branch: string } {
  // 월지는 고정: 1월=인, 2월=묘, 3월=진, 4월=사, 5월=오, 6월=미,
  // 7월=신, 8월=유, 9월=술, 10월=해, 11월=자, 12월=축
  const monthBranches = ['인', '묘', '진', '사', '오', '미', '신', '유', '술', '해', '자', '축'];
  const branchIndex = (month + 1) % 12; // 1월부터 시작하도록 조정
  
  // 월간 계산 (년간에 따라 결정)
  const yearStem = getYearPillar(year).stem;
  const yearStemIndex = heavenlyStems.indexOf(yearStem);
  
  // 월간 공식: (년간 * 2 + 월수) % 10
  const monthStemIndex = (yearStemIndex * 2 + month) % 10;
  
  return {
    stem: heavenlyStems[monthStemIndex],
    branch: monthBranches[month - 1]
  };
}

// 일주 계산 (1900년 1월 1일 = 갑자일 기준)
function getDayPillar(date: Date): { stem: string; branch: string } {
  const baseDate = new Date(1900, 0, 1); // 1900년 1월 1일 (갑자일)
  const daysDiff = Math.floor((date.getTime() - baseDate.getTime()) / (1000 * 60 * 60 * 24));
  
  const stemIndex = daysDiff % 10;
  const branchIndex = daysDiff % 12;
  
  return {
    stem: heavenlyStems[(stemIndex + 10) % 10],
    branch: earthlyBranches[(branchIndex + 12) % 12]
  };
}

// 시주 계산
function getHourPillar(date: Date, hour: number): { stem: string; branch: string } {
  // 시지 계산: 23-01시=자, 01-03시=축, 03-05시=인, ...
  const hourBranches = ['자', '축', '인', '묘', '진', '사', '오', '미', '신', '유', '술', '해'];
  const branchIndex = Math.floor((hour + 1) / 2) % 12;
  
  // 시간 계산 (일간에 따라 결정)
  const dayStem = getDayPillar(date).stem;
  const dayStemIndex = heavenlyStems.indexOf(dayStem);
  
  // 시간 공식: (일간 * 2 + 시지순서) % 10
  const hourStemIndex = (dayStemIndex * 2 + branchIndex) % 10;
  
  return {
    stem: heavenlyStems[hourStemIndex],
    branch: hourBranches[branchIndex]
  };
}

// 사주팔자 계산
export interface FourPillars {
  year: { stem: string; branch: string; combined: string };
  month: { stem: string; branch: string; combined: string };
  day: { stem: string; branch: string; combined: string };
  hour: { stem: string; branch: string; combined: string };
}

export function calculateFourPillars(birthDate: string, birthTime: string): FourPillars {
  const date = new Date(birthDate);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  
  // 시간 파싱 (HH:mm 형식)
  const [hourStr] = birthTime.split(':');
  const hour = parseInt(hourStr, 10);
  
  const yearPillar = getYearPillar(year);
  const monthPillar = getMonthPillar(year, month);
  const dayPillar = getDayPillar(date);
  const hourPillar = getHourPillar(date, hour);
  
  return {
    year: {
      stem: yearPillar.stem,
      branch: yearPillar.branch,
      combined: `${yearPillar.stem}${yearPillar.branch}`
    },
    month: {
      stem: monthPillar.stem,
      branch: monthPillar.branch,
      combined: `${monthPillar.stem}${monthPillar.branch}`
    },
    day: {
      stem: dayPillar.stem,
      branch: dayPillar.branch,
      combined: `${dayPillar.stem}${dayPillar.branch}`
    },
    hour: {
      stem: hourPillar.stem,
      branch: hourPillar.branch,
      combined: `${hourPillar.stem}${hourPillar.branch}`
    }
  };
}

// 오행 비율 계산
export interface ElementBalance {
  wood: number;
  fire: number;
  earth: number;
  metal: number;
  water: number;
}

export function calculateElementBalance(fourPillars: FourPillars): ElementBalance {
  const elements = { wood: 0, fire: 0, earth: 0, metal: 0, water: 0 };
  
  // 각 기둥의 천간과 지지에서 오행 추출
  const pillars = [fourPillars.year, fourPillars.month, fourPillars.day, fourPillars.hour];
  
  pillars.forEach(pillar => {
    const stemElement = stemElements[pillar.stem];
    const branchElement = branchElements[pillar.branch];
    
    // 천간 오행 (가중치 1)
    if (stemElement === '목') elements.wood += 1;
    else if (stemElement === '화') elements.fire += 1;
    else if (stemElement === '토') elements.earth += 1;
    else if (stemElement === '금') elements.metal += 1;
    else if (stemElement === '수') elements.water += 1;
    
    // 지지 오행 (가중치 1)
    if (branchElement === '목') elements.wood += 1;
    else if (branchElement === '화') elements.fire += 1;
    else if (branchElement === '토') elements.earth += 1;
    else if (branchElement === '금') elements.metal += 1;
    else if (branchElement === '수') elements.water += 1;
  });
  
  return elements;
}

// 오행 분석
export function analyzeElements(balance: ElementBalance): {
  strongest: string;
  weakest: string;
  description: string;
} {
  const elementNames: Record<string, string> = {
    wood: '목(木)',
    fire: '화(火)',
    earth: '토(土)',
    metal: '금(金)',
    water: '수(水)'
  };
  
  const entries = Object.entries(balance);
  const strongest = entries.reduce((a, b) => (a[1] > b[1] ? a : b));
  const weakest = entries.reduce((a, b) => (a[1] < b[1] ? a : b));
  
  const descriptions: Record<string, string> = {
    wood: '목 기운이 강하면 성장과 확장의 에너지가 풍부합니다. 창의적이고 진취적인 성향을 가집니다.',
    fire: '화 기운이 강하면 열정과 활력이 넘칩니다. 리더십과 카리스마가 있습니다.',
    earth: '토 기운이 강하면 안정적이고 신뢰감이 있습니다. 포용력과 인내심이 뛰어납니다.',
    metal: '금 기운이 강하면 결단력과 추진력이 있습니다. 원칙과 정의를 중시합니다.',
    water: '수 기운이 강하면 지혜롭고 유연합니다. 적응력과 통찰력이 뛰어납니다.'
  };
  
  return {
    strongest: elementNames[strongest[0]],
    weakest: elementNames[weakest[0]],
    description: descriptions[strongest[0]]
  };
}

// 십신 계산 (일간 기준)
function getTenGod(dayStem: string, targetStem: string): string {
  const dayElement = stemToElement[dayStem];
  const targetElement = stemToElement[targetStem];
  const dayYY = stemYinYang[dayStem];
  const targetYY = stemYinYang[targetStem];
  const sameYY = dayYY === targetYY;

  if (dayElement === targetElement) return sameYY ? '비견(比肩)' : '겁재(劫財)';
  if (elementGenerates[dayElement] === targetElement) return sameYY ? '식신(食神)' : '상관(傷官)';
  if (elementDestroys[dayElement] === targetElement) return sameYY ? '편재(偏財)' : '정재(正財)';
  if (elementDestroys[targetElement] === dayElement) return sameYY ? '편관(偏官)' : '정관(正官)';
  if (elementGenerates[targetElement] === dayElement) return sameYY ? '편인(偏印)' : '정인(正印)';
  return '—';
}

// 지지 십신 (지지 대표 오행 기준, 음양은 지지 자체 음양 사용)
const branchYinYang: Record<string, '+' | '-'> = {
  '자': '-', '축': '-', '인': '+', '묘': '-',
  '진': '+', '사': '+', '오': '-', '미': '-',
  '신': '+', '유': '-', '술': '+', '해': '+',
};

function getBranchTenGod(dayStem: string, branch: string): string {
  const dayElement = stemToElement[dayStem];
  const branchElement = branchToElement[branch];
  if (!branchElement) return '—';
  const dayYY = stemYinYang[dayStem];
  const branchYY = branchYinYang[branch];
  const sameYY = dayYY === branchYY;

  if (dayElement === branchElement) return sameYY ? '비견' : '겁재';
  if (elementGenerates[dayElement] === branchElement) return sameYY ? '식신' : '상관';
  if (elementDestroys[dayElement] === branchElement) return sameYY ? '편재' : '정재';
  if (elementDestroys[branchElement] === dayElement) return sameYY ? '편관' : '정관';
  if (elementGenerates[branchElement] === dayElement) return sameYY ? '편인' : '정인';
  return '—';
}

export interface TenGodEntry {
  pillar: string;
  stem: string;
  stemGod: string;
  branch: string;
  branchGod: string;
}

export interface TenGodAnalysis {
  dayStem: string;
  entries: TenGodEntry[];
  dominantGods: { god: string; count: number; shortMeaning: string }[];
}

const tenGodMeanings: Record<string, string> = {
  '비견': '독립심·경쟁·자존심',
  '겁재': '추진력·과감성·재물변동',
  '식신': '표현력·먹을복·여유',
  '상관': '창의성·예술적 재능·변화',
  '편재': '사업수완·이성인연·유동재물',
  '정재': '성실·근면·안정재물',
  '편관': '권위·책임감·도전',
  '정관': '명예·사회적 지위·도덕성',
  '편인': '학문·예술·종교적 소양',
  '정인': '학문·어머니·보호와 지원',
};

export function calculateTenGods(fp: FourPillars): TenGodAnalysis {
  const dayStem = fp.day.stem;

  const entries: TenGodEntry[] = [
    {
      pillar: '년주(年柱)',
      stem: fp.year.stem,
      stemGod: getTenGod(dayStem, fp.year.stem),
      branch: fp.year.branch,
      branchGod: getBranchTenGod(dayStem, fp.year.branch),
    },
    {
      pillar: '월주(月柱)',
      stem: fp.month.stem,
      stemGod: getTenGod(dayStem, fp.month.stem),
      branch: fp.month.branch,
      branchGod: getBranchTenGod(dayStem, fp.month.branch),
    },
    {
      pillar: '일주(日柱)',
      stem: fp.day.stem,
      stemGod: '일원(日元)',
      branch: fp.day.branch,
      branchGod: getBranchTenGod(dayStem, fp.day.branch),
    },
    {
      pillar: '시주(時柱)',
      stem: fp.hour.stem,
      stemGod: getTenGod(dayStem, fp.hour.stem),
      branch: fp.hour.branch,
      branchGod: getBranchTenGod(dayStem, fp.hour.branch),
    },
  ];

  // 도수 집계 (일원 제외)
  const godCount: Record<string, number> = {};
  entries.forEach((e) => {
    const gods = [e.stemGod, e.branchGod].filter(
      (g) => g !== '—' && g !== '일원(日元)'
    );
    gods.forEach((g) => {
      const key = g.replace(/\(.*\)/, '').trim();
      godCount[key] = (godCount[key] || 0) + 1;
    });
  });

  const dominantGods = Object.entries(godCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([god, count]) => ({
      god,
      count,
      shortMeaning: tenGodMeanings[god] ?? '',
    }));

  return { dayStem, entries, dominantGods };
}